import { useMutation, useQuery, useQueryClient } from "react-query";
import { useClient } from "../providers/auth-context";
import { SupplierSchema } from "../interfaces/user.interface";

interface SuppliersListResponse {
  count: number;
  data: Array<SupplierSchema>;
}

// Editable subset of the supplier record. Only the fields sent are updated,
// omitted fields are left unchanged.
interface SupplierChangesSchema {
  rmId?: string;
  name?: string;
  email?: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  postCode?: string;
  town?: string;
  country?: string;
  office?: string;
  allowRegisterToPortal?: boolean;
}

interface SupplierUpdateResponse {
  status: string;
  result: {
    userId: string;
    customerId: number;
  };
}

const apimRootURL = () =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.env.REACT_APP_API_URL.split("/ims")[0];

function useSuppliersList(name: string) {
  const client = useClient<SuppliersListResponse>();
  const result = useQuery({
    queryKey: ["suppliersList", { name }],
    queryFn: () =>
      client(`frontoffice/VintradeSuppliers${name && `?name=${encodeURIComponent(name)}`}`, {
        customURL: apimRootURL(),
      }).then((data) => data),
  });

  return { isLoading: result.isLoading, data: result.data };
}

function useUpdateSupplier() {
  const client = useClient<SupplierUpdateResponse>();
  const queryClient = useQueryClient();

  return useMutation(
    ({ userId, changes }: { userId: string; changes: SupplierChangesSchema }) =>
      client(`vintrade-customer/Customers/Suppliers/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(changes),
        customURL: apimRootURL(),
      }),
    {
      // refetch so the next read of the supplier returns the values we just wrote
      onSettled: () => {
        queryClient.invalidateQueries("suppliersList");
      },
    }
  );
}

export { useSuppliersList, useUpdateSupplier };
export type { SupplierChangesSchema };
